import React from "react";
import { Joyride, STATUS } from "react-joyride";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";
import { speak as ariaSpeak, cancelSpeech } from "@/lib/ariaSpeech";

export const TOUR_KEY = "yl_tour_seen_v1";
export const DEMO_TOUR_KEY = "yl_demo_tour_seen";

const demoSteps = [
  {
    target: '[data-testid="demo-badge"]',
    title: "You're in Demo Mode",
    content: "Everything you'll see now is realistic, ready-made sample data — three client programs, live tenders with full proposals, and SAP connectors. Nothing here affects real data. Let me walk you through it.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: '[data-tour="nav-workspace"]',
    title: "Sample client programs",
    content: "Three live-looking engagements (Apollo Hospitals, Bright Future Education, Nova Retail) — each with an analysed requirement meeting, branded BRD & Integration specs, derived SAP interfaces and a health badge. Open any to explore.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-rfq"]',
    title: "RFQ → full proposal pack",
    content: "Three sample tenders. Bright Future is fully worked: a clause-cited RFQ profile, win themes, and branded Technical, Project & Commercial volumes — plus a one-click Full Proposal Pack PDF.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-connectors"]',
    title: "Executable SAP connectors",
    content: "Configure S/4HANA OData, BAPI/RFC or IDoc connectors, test them live and run interfaces — with a health dashboard that pings every connector on a schedule. The sample shows two systems up and one down.",
    placement: "right",
  },
  {
    target: '[data-tour="help-avatar"]',
    title: "Ask Aria anything",
    content: "Your AI guide answers questions about the product and your data, and can speak her answers. Perfect for a hands-free walkthrough.",
    placement: "left",
  },
  {
    target: '[data-testid="demo-mode-toggle"]',
    title: "Flip back anytime",
    content: "Turn Demo Mode off here to return to your own private workspace. That's the tour — enjoy exploring!",
    placement: "bottom",
  },
];

const steps = [
  {
    target: '[data-tour="sidebar-nav"]',
    title: "Welcome to Yanthra Labs Business360",
    content: "This is your command centre. Everything you create lives here and is private to you. Let me show you around in 20 seconds.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: '[data-tour="nav-workspace"]',
    title: "Your Workspace",
    content: "Your dashboard of client projects. Open a project to reach its meetings, documents, plan and health badges.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-new-project"]',
    title: "Meetings → Deliverables",
    content: "Start a project here, then inside it create a meeting: record or upload audio in English or Indic languages, let the Quality Guard validate it, and generate a branded 18-section Minutes of Meeting plus BRD/FSD and more.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-rfq"]',
    title: "RFQ-to-Proposal Studio",
    content: "Upload a tender and the AI extracts a clause-cited RFQ profile, scores bid/no-bid, draws the solution architecture, and writes branded Technical, Project and Commercial proposals.",
    placement: "right",
  },
  {
    target: '[data-tour="search"]',
    title: "Find anything instantly",
    content: "Jump to any project, meeting or document. Tip: press ⌘K / Ctrl-K anywhere.",
    placement: "bottom",
  },
  {
    target: '[data-tour="help-avatar"]',
    title: "Meet Aria, your AI guide",
    content: "Stuck? Open Aria anytime. She answers questions about the product, your own projects and proposals — and can speak her answers as a live avatar.",
    placement: "left",
  },
  {
    target: '[data-tour="help-tour"]',
    title: "Replay this tour",
    content: "You can restart this guided tour whenever you like from here. That's it — enjoy the studio!",
    placement: "bottom",
  },
];

export default function ProductTour({ run, onFinish, demo = false }) {
  const spokenRef = React.useRef(-1);
  const advancedRef = React.useRef(-1);
  const advanceTimer = React.useRef(null);
  const activeSteps = demo ? demoSteps : steps;
  const [narrateMuted, setNarrateMuted] = React.useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("yl_aria_muted") === "1"
  );
  const [autoPlay, setAutoPlay] = React.useState(true);
  const autoPlayRef = React.useRef(true);
  const mutedRef = React.useRef(narrateMuted);
  React.useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);
  React.useEffect(() => { mutedRef.current = narrateMuted; }, [narrateMuted]);

  const clearAdvance = () => { if (advanceTimer.current) { clearTimeout(advanceTimer.current); advanceTimer.current = null; } };
  const clickPrimary = (index) => {
    if (advancedRef.current === index) return;         // advance each step at most once
    advancedRef.current = index;
    document.querySelector('.react-joyride__tooltip button[data-action="primary"]')?.click();
  };
  const autoClick = (index) => { if (autoPlayRef.current) clickPrimary(index); };
  const scheduleAdvance = (index) => {
    clearAdvance();
    const s = activeSteps[index];
    if (!s) return;
    const words = `${s.title}. ${s.content}`.split(/\s+/).length;
    const ms = Math.min(16000, Math.max(3500, (words / 2.3) * 1000)) + 800;  // fallback / muted path
    advanceTimer.current = setTimeout(() => autoClick(index), ms);
  };

  React.useEffect(() => {
    if (run) { spokenRef.current = -1; advancedRef.current = -1; }
    else { cancelSpeech(); clearAdvance(); }
    return () => { cancelSpeech(); clearAdvance(); };
  }, [run]);

  const handle = (data) => {
    const { status, index, lifecycle } = data;
    if (demo && lifecycle === "tooltip" && index !== spokenRef.current) {
      spokenRef.current = index;
      advancedRef.current = -1;
      clearAdvance();
      const s = activeSteps[index];
      if (s) {
        if (!mutedRef.current) ariaSpeak(`${s.title}. ${s.content}`, { onEnd: () => autoClick(index) });
        if (autoPlayRef.current) scheduleAdvance(index);
      }
    }
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      clearAdvance();
      cancelSpeech();
      localStorage.setItem(demo ? DEMO_TOUR_KEY : TOUR_KEY, "1");
      onFinish?.();
    }
  };

  const toggleNarrateMute = () => {
    const nv = !narrateMuted;
    setNarrateMuted(nv);
    try { localStorage.setItem("yl_aria_muted", nv ? "1" : "0"); } catch { /* noop */ }
    if (nv) cancelSpeech();
    else { const s = activeSteps[spokenRef.current]; if (s) ariaSpeak(`${s.title}. ${s.content}`, { onEnd: () => autoClick(spokenRef.current) }); }
  };

  const toggleAutoPlay = () => {
    const nv = !autoPlay;
    setAutoPlay(nv);
    if (!nv) clearAdvance();
    else scheduleAdvance(spokenRef.current);
  };

  return (
    <>
      {demo && run && (
        <div className="fixed bottom-5 left-5 z-[10001] flex items-center gap-2" data-testid="demo-tour-controls">
          <button onClick={toggleAutoPlay} data-testid="demo-autoplay-toggle"
            aria-label={autoPlay ? "Pause auto-play" : "Resume auto-play"}
            className="inline-flex items-center gap-2 rounded-full bg-[#29abe2] text-white shadow-xl px-3.5 py-2 text-xs font-semibold hover:bg-[#1a7bb0] transition-colors">
            {autoPlay ? <Pause size={15} /> : <Play size={15} />}
            {autoPlay ? "Auto-playing" : "Paused"}
          </button>
          <button onClick={toggleNarrateMute} data-testid="demo-narration-toggle"
            aria-label={narrateMuted ? "Turn narration on" : "Mute narration"}
            className="inline-flex items-center gap-2 rounded-full bg-[#0f2557] text-white shadow-xl px-3.5 py-2 text-xs font-semibold hover:bg-[#1a3570] transition-colors">
            {narrateMuted ? <VolumeX size={15} /> : <Volume2 size={15} className="animate-pulse" />}
            {narrateMuted ? "Narration off" : "Narrating"}
          </button>
        </div>
      )}
    <Joyride
      steps={demo ? demoSteps : steps}
      run={run}
      continuous
      onEvent={handle}
      locale={{ back: "Back", close: "Close", last: "Finish", next: "Next", skip: "Skip tour" }}
      options={{
        showProgress: true,
        skipBeacon: true,
        skipScroll: true,
        primaryColor: "#29abe2",
        textColor: "#0f2557",
        backgroundColor: "#ffffff",
        arrowColor: "#ffffff",
        zIndex: 10000,
        buttons: ["back", "skip", "primary"],
      }}
      styles={{
        tooltip: { borderRadius: 14, padding: 18 },
        tooltipTitle: { fontSize: 16, fontWeight: 700, color: "#0f2557" },
        tooltipContent: { fontSize: 13.5, lineHeight: 1.5, color: "#334155" },
        buttonPrimary: { borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 600 },
        buttonBack: { color: "#64748b", fontSize: 13 },
        buttonSkip: { color: "#94a3b8", fontSize: 12 },
      }}
    />
    </>
  );
}
